import Guid from "common/values/guid/guid";
import MarketplaceTeam from "marketplace/entities/marketplace-team/marketplace-team";
import EntityVendorRepresentative from "work/entities/entity-vendor-representative/entity-vendor-representative";
import EntityMember from "work/values/entity-member/entity-member";

export interface VendorTeamJSON {
  leader?: {
    entityId: string;
    userId: string;
  };
  members: Array<{
    userId: string;
    entityId: string;
  }>;
  marketplaceId?: string;
}

export default class VendorTeam {
  leader: EntityVendorRepresentative | undefined;
  members: EntityMember[];
  marketplaceId?: Guid;

  constructor(leader: EntityVendorRepresentative | undefined, members: EntityMember[], marketplaceId?: Guid) {
    this.leader = leader;
    this.members = members;
    this.marketplaceId = marketplaceId;
  }

  public static FromMarketplaceTeam(team: MarketplaceTeam): VendorTeam | undefined {
    if (!team.leader?.userId || !team.leader?.entityId) return;
    const leader = new EntityVendorRepresentative(
      team.leader.userId,
      team.leader.entityId
    );
    const members: EntityMember[] = [];
    for (const member of team.memberships) {
      if (member.userId && member.entityId) {
        members.push(new EntityMember(
          member.userId,
          member.entityId
        ));
      } else {
        throw new Error("Member userId and entityId are required");
      }
      return new VendorTeam(
        leader,
        members,
        team.id
      );
    }
  }

  public isEqualTo(other?: unknown): boolean {
    if (!other || !(other instanceof VendorTeam)) {
      return false;
    }
    if (!this.leader && other.leader) {
      return false;
    }
    if (this.leader && !other.leader) {
      return false;
    }
    if (this.leader && !this.leader?.isEqualTo(other.leader)) {
      return false;
    }
    if (this.members.length !== other.members.length) {
      return false;
    }
    const sortedMemberUserIds = this.members.map(m => m.userId).toSorted((a, b) => a.value.localeCompare(b.value));
    const sortedOtherMemberUserIds = other.members.map(m => m.userId).toSorted((a, b) => a.value.localeCompare(b.value));
    for (let i = 0; i < sortedMemberUserIds.length; i++) {
      if (!sortedMemberUserIds[i].isEqualTo(sortedOtherMemberUserIds[i])) {
        return false;
      }
    }

    return true;
  }

  public clone(): VendorTeam {
    return new VendorTeam(
      this.leader?.clone(),
      this.members.map(member => member.clone()),
      this.marketplaceId
    );
  }

  public toJSON(): VendorTeamJSON {
    return {
      leader: this.leader ? {
        entityId: this.leader.entityId.value,
        userId: this.leader.userId.value
      } : undefined,
      members: this.members.map((member) => {
        return {
          userId: member.userId.value,
          entityId: member.entityId.value
        };
      }),
      marketplaceId: this.marketplaceId?.value
    };
  }

  public static fromJSON(object: VendorTeamJSON): VendorTeam | undefined {
    if (!object) return undefined;
    const leader = object.leader ? new EntityVendorRepresentative(
      new Guid(object.leader.userId),
      new Guid(object.leader.entityId)
    ) : undefined;
    const members = object.members.map(memberInfo => new EntityMember(
      new Guid(memberInfo.userId),
      new Guid(memberInfo.entityId)
    ));
    const marketplaceId = object.marketplaceId ? new Guid(object.marketplaceId) : undefined;
    
    return new VendorTeam(
      leader,
      members,
      marketplaceId
    );
  }
}
